import { useRef, useState } from "react";
import { Upload, X, Star } from "lucide-react";
import { encodePhotos, MAX_PHOTOS } from "../utils/photos";

export function usePhotoUploader({ max = MAX_PHOTOS, notify } = {}) {
  const [photos, setPhotos] = useState([]);
  const [thumbnailIndex, setThumbnailIndex] = useState(0);
  const [encoding, setEncoding] = useState(false);
  const inputRef = useRef(null);

  function resetInput() {
    if (inputRef.current) inputRef.current.value = "";
  }

  async function addFiles(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    setEncoding(true);
    try {
      const { encoded, message } = await encodePhotos(files, { max, existing: photos.length });
      if (encoded.length) setPhotos((prev) => [...prev, ...encoded].slice(0, max));
      if (message) notify?.(message);
    } finally {
      setEncoding(false);
      resetInput();
    }
  }

  function removePhoto(index) {
    setPhotos(photos.filter((_, i) => i !== index));
    setThumbnailIndex((current) => (current === index ? 0 : current > index ? current - 1 : current));
  }

  function setPhotosFromRow(row) {
    setPhotos(row?.photos || []);
    setThumbnailIndex(Math.max(0, Math.min(row?.thumbnailIndex ?? 0, (row?.photos?.length || 1) - 1)));
  }

  function clearPhotos() {
    setPhotos([]);
    setThumbnailIndex(0);
    resetInput();
  }

  return {
    photos,
    thumbnailIndex,
    setThumbnailIndex,
    encoding,
    inputRef,
    remaining: Math.max(0, max - photos.length),
    addFiles,
    removePhoto,
    setPhotosFromRow,
    clearPhotos,
  };
}

export function PhotoUploader({ label = "Photos", photos, thumbnailIndex, setThumbnailIndex, remaining, encoding, inputRef, addFiles, removePhoto, className = "image-upload-area" }) {
  return (
    <div className={className}>
      <p className="section-label">
        {label} ({photos.length}/{MAX_PHOTOS})
      </p>
      <div className="image-previews">
        {photos.map((src, i) => (
          <div
            className="image-preview"
            key={i}
            onClick={() => setThumbnailIndex(i)}
            style={{ cursor: "pointer", outline: thumbnailIndex === i ? "3px solid #f59e0b" : "1px solid var(--border)", outlineOffset: 1 }}
            title="Click to set as thumbnail"
          >
            {thumbnailIndex === i && (
              <span style={{ position: "absolute", top: 2, left: 2, zIndex: 2, background: "#f59e0b", color: "#fff", borderRadius: 6, padding: "1px 5px", fontSize: 10, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 2 }}>
                <Star size={10} /> THUMB
              </span>
            )}
            <span style={{ position: "absolute", top: 2, right: 2, zIndex: 2, background: "#0f172a", color: "#fff", borderRadius: 6, padding: "1px 5px", fontSize: 10 }}>#{i + 1}</span>
            <img src={src} alt={`Photo ${i + 1}`} />
            <button type="button" className="image-remove-btn" onClick={(e) => { e.stopPropagation(); removePhoto(i); }}>
              <X size={14} />
            </button>
          </div>
        ))}
      </div>

      {remaining > 0 && (
        <div className="image-dropzone" onClick={() => !encoding && inputRef.current?.click()}>
          <Upload size={24} />
          <span>{encoding ? "Processing photos..." : "Click to upload photos"}</span>
          <span className="image-hint">
            Up to {remaining} more · {encoding ? "please wait" : "resized and stored as base64"}
          </span>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        disabled={encoding}
        onChange={(e) => addFiles(e.target.files)}
        style={{ display: "none" }}
      />

      {photos.length > 0 ? (
        <p className="image-hint" style={{ marginTop: 12, fontSize: 12, color: "#64748b" }}>
          Click any photo to set as thumbnail · Current: Photo #{thumbnailIndex + 1}
        </p>
      ) : null}
    </div>
  );
}
